import type { UseTRPCMutationResult } from '@trpc/react-query/shared';
import type { TrpcMutationAdapter } from '@/lib/trpc/mutation-adapter';
import { createTrpcMutationAdapter } from '@/lib/trpc/mutation-adapter';
import { getRecordValue, isRecord, isRecordLike } from '@/lib/utils/type-guards';

export type VoidOrPromiseVoid = void | Promise<void>;

export interface TrpcUtilitiesAdapter {
  social: {
    getCookiesStatus: {
      invalidate: () => VoidOrPromiseVoid;
    };
  };
}

type NormalizedQueryResult = {
  data?: unknown;
  isSuccess?: boolean;
  isError?: boolean;
  isLoading?: boolean;
  refetch?: () => Promise<unknown>;
  error?: unknown;
};

export interface TrpcSocialAdapter {
  createCancelSyncMutation: (options?: Record<string, unknown>) => TrpcMutationAdapter;
  createDeleteCookiesMutation: (options?: Record<string, unknown>) => TrpcMutationAdapter;
  createTriggerSyncMutation: (options?: Record<string, unknown>) => TrpcMutationAdapter;
  createUpdateCookiesMutation: (options?: Record<string, unknown>) => TrpcMutationAdapter;
  getCookiesStatusQuery: (input: undefined, options?: Record<string, unknown>) => NormalizedQueryResult;
  getSyncStatusQuery: (input: undefined, options?: Record<string, unknown>) => NormalizedQueryResult;
  useUtils: () => TrpcUtilitiesAdapter;
}

type QueryFactory = { useQuery: (...arguments_: unknown[]) => unknown };
type MutationFactory = {
  useMutation: (...arguments_: unknown[]) => UseTRPCMutationResult<unknown, unknown, unknown, unknown>;
};

const isFunction = (value: unknown): value is (...arguments_: unknown[]) => unknown => typeof value === 'function';

const isQueryFactory = (factory: unknown): factory is QueryFactory =>
  isRecordLike(factory) && isFunction(getRecordValue(factory, 'useQuery'));

const isMutationFactory = (factory: unknown): factory is MutationFactory =>
  isRecordLike(factory) && isFunction(getRecordValue(factory, 'useMutation'));

const expectQueryFactory = (factory: unknown, label: string): QueryFactory => {
  if (!isQueryFactory(factory)) {
    throw new TypeError(`${label} unavailable`);
  }
  return factory;
};

const expectMutationFactory = (factory: unknown, label: string): MutationFactory => {
  if (!isMutationFactory(factory)) {
    throw new TypeError(`${label} unavailable`);
  }
  return factory;
};

const normalizeQueryResult = (result: unknown): NormalizedQueryResult => {
  if (!isRecordLike(result)) {
    return {};
  }

  const normalized: NormalizedQueryResult = {};

  const dataValue = getRecordValue(result, 'data');
  if (dataValue !== undefined) {
    normalized.data = dataValue;
  }

  const isSuccessValue = getRecordValue(result, 'isSuccess');
  if (typeof isSuccessValue === 'boolean') {
    normalized.isSuccess = isSuccessValue;
  }

  const isErrorValue = getRecordValue(result, 'isError');
  if (typeof isErrorValue === 'boolean') {
    normalized.isError = isErrorValue;
  }

  const isLoadingValue = getRecordValue(result, 'isLoading');
  if (typeof isLoadingValue === 'boolean') {
    normalized.isLoading = isLoadingValue;
  }

  const refetchCandidate = getRecordValue(result, 'refetch');
  if (isFunction(refetchCandidate)) {
    normalized.refetch = () => Promise.resolve(refetchCandidate());
  }

  const errorValue = getRecordValue(result, 'error');
  if (errorValue !== undefined) {
    normalized.error = errorValue;
  }

  return normalized;
};

export function createTrpcSocialAdapter(candidate: unknown, label = 'tRPC client'): TrpcSocialAdapter {
  if (!isRecordLike(candidate)) {
    throw new TypeError(`${label} unavailable`);
  }

  const useUtilitiesRaw = getRecordValue(candidate, 'useUtils');
  if (!isFunction(useUtilitiesRaw)) {
    throw new TypeError(`${label} utils helper missing`);
  }

  const socialGroup = getRecordValue(candidate, 'social');
  if (!isRecordLike(socialGroup)) {
    throw new TypeError(`${label} social router unavailable`);
  }

  const getCookiesStatusFactory = expectQueryFactory(
    getRecordValue(socialGroup, 'getCookiesStatus'),
    `${label} social.getCookiesStatus`
  );
  const getSyncStatusFactory = expectQueryFactory(
    getRecordValue(socialGroup, 'getSyncStatus'),
    `${label} social.getSyncStatus`
  );
  const updateCookiesFactory = expectMutationFactory(
    getRecordValue(socialGroup, 'updateCookies'),
    `${label} social.updateCookies`
  );
  const deleteCookiesFactory = expectMutationFactory(
    getRecordValue(socialGroup, 'deleteCookies'),
    `${label} social.deleteCookies`
  );
  const triggerSyncFactory = expectMutationFactory(
    getRecordValue(socialGroup, 'triggerSync'),
    `${label} social.triggerSync`
  );
  const cancelSyncFactory = expectMutationFactory(
    getRecordValue(socialGroup, 'cancelSync'),
    `${label} social.cancelSync`
  );

  return {
    useUtils: () => {
      const utilitiesValue = useUtilitiesRaw.call(candidate);
      if (!isRecord(utilitiesValue)) {
        throw new TypeError(`${label} utils social helpers unavailable`);
      }
      const utilitiesSocial = getRecordValue(utilitiesValue, 'social');
      if (!isRecord(utilitiesSocial)) {
        throw new TypeError(`${label} utils social helpers unavailable`);
      }
      const cookiesUtilities = getRecordValue(utilitiesSocial, 'getCookiesStatus');
      if (!isRecord(cookiesUtilities)) {
        throw new TypeError(`${label} utils social.getCookiesStatus.invalidate unavailable`);
      }
      const invalidate = getRecordValue(cookiesUtilities, 'invalidate');
      if (!isFunction(invalidate)) {
        throw new TypeError(`${label} utils social.getCookiesStatus.invalidate unavailable`);
      }

      return {
        social: {
          getCookiesStatus: {
            invalidate: () => {
              invalidate.call(cookiesUtilities);
            },
          },
        },
      } satisfies TrpcUtilitiesAdapter;
    },
    getCookiesStatusQuery: (input: undefined, options?: Record<string, unknown>) =>
      normalizeQueryResult(getCookiesStatusFactory.useQuery(input, options)),
    getSyncStatusQuery: (input: undefined, options?: Record<string, unknown>) =>
      normalizeQueryResult(getSyncStatusFactory.useQuery(input, options)),
    createUpdateCookiesMutation: (options?: Record<string, unknown>) =>
      createTrpcMutationAdapter(updateCookiesFactory, options),
    createDeleteCookiesMutation: (options?: Record<string, unknown>) =>
      createTrpcMutationAdapter(deleteCookiesFactory, options),
    createTriggerSyncMutation: (options?: Record<string, unknown>) =>
      createTrpcMutationAdapter(triggerSyncFactory, options),
    createCancelSyncMutation: (options?: Record<string, unknown>) =>
      createTrpcMutationAdapter(cancelSyncFactory, options),
  };
}
